// @flow

import { toJS, autorun } from 'mobx';
import {observer} from 'mobx-react';
import React, {  Component } from 'react';
import Select from 'react-select';
import EditorLink from '~/src/components/EditorLink';
import t from '~/src/services/i18n';
import RadioButtons from '~/src/components/RadioButtons';
import ColorPicker from '~/src/components/ColorPicker';
import PageIntro from '~/src/components/PageIntro';
import trackEvent from '~/src/services/analytics';

declare var cloudinary: any;

import './EasyCustomization.scss';

type Props = {
  config: Object,
  setValue:Function,
  setFormConfig: Function,
  widgetParams: Object,
  isMobile?: boolean
}

const layoutOptions = [
  {
    key: 'expanded-1',
    value: {
      mode: cloudinary.galleryWidgetInfo.ENUMS.DisplayMode.EXPANDED,
      columns: 1
    },
    label: t('oneColumn'),
    icon: 'Icons/layout-modern'
  },
  {
    key: 'expanded-2',
    value: {
      mode: cloudinary.galleryWidgetInfo.ENUMS.DisplayMode.EXPANDED,
      columns: 2
    },
    label: t('twoColumn'),
    icon: 'Icons/layout-grid-2-column'
  },
  {
    key: 'expanded-3',
    value: {
      mode: cloudinary.galleryWidgetInfo.ENUMS.DisplayMode.EXPANDED,
      columns: 3
    },
    label: t('threeColumn'),
    icon: 'Icons/layout-grid-3-column'
  },
  {
    key: 'classic-1',
    value: {
      mode: cloudinary.galleryWidgetInfo.ENUMS.DisplayMode.CLASSIC,
      columns: 1
    },
    label: t('Classic'),
    icon: 'Icons/layout-classic'
  }
];

const carouselLocations = (currentLayout) => [
  {
    value: 'right',
    label: t('Right'),
  },
  {
    value: 'left',
    label: t('Left'),
  },
  {
    value: 'top',
    label: t('Top'),
    isDisabled: currentLayout == 'expanded'
  },
  {
    value: 'bottom',
    label: t('Bottom'),
    isDisabled: currentLayout == 'expanded'
  },
];

const btnShapes = cloudinary.galleryWidgetInfo.ENUMS.ButtonShape;


const shapeOptions = Object.keys(btnShapes).map(option => ({
  value: btnShapes[option],
  icon: 'Icons/' + btnShapes[option],
}));

const eventNames = {
  carouselLocation: 'carousel location',
  shape: 'edit button shape',
  color: 'background',
  iconColor: 'icon'
};

@observer
class EasyCustomization extends Component<Props> {
  myWidget = {};
  updateConfigDisposer = false;
  init = false;

  componentDidMount(){
    this.myWidget = cloudinary.galleryWidget(toJS(this.props.config));
    this.myWidget.render();

    this.updateConfigDisposer = autorun(() => {
      const params = this.props.widgetParams;
      if (this.init) {
        this.myWidget.update(params);
      }
      setTimeout(() => { this.init = true; });
    });
  }

  componentWillUnmount() {
    if (this.updateConfigDisposer) {
      this.updateConfigDisposer();
    }
    this.myWidget.destroy();
  }

  setValue = (key: string, value: string) => {
    this.props.setValue(key, value);
    key !== 'mode' && key !== 'columns' && trackEvent({ action: eventNames[key], label: value });
  };

  render() {
    const displayProps = this.props.config.displayProps;
    const navigationButtonProps = this.props.config.navigationButtonProps;
    const carouselLocationOpts = carouselLocations(displayProps.mode);
    return (
      <div className="section easy-customization">
        <PageIntro page={'product'} />
        <h1>{t('easyToCustomize')}</h1>
        <div className="content">
          <div className="gallery">
            <div id="easy-customization-gallery"></div>
          </div>
          <div className="gallery-settings">
            <div className="transformation-box layout-type">
              <h3>{t('Layout')}</h3>
              <RadioButtons
                items={layoutOptions}
                value={displayProps.mode + '-' + displayProps.columns}
                setValue={(name, val) => {
                  this.setValue('mode', val.mode);
                  this.setValue('columns', val.columns || 1);
                  trackEvent({ action: 'edit layout', label: val.mode + '-' + val.columns });
                }}
                propertyName={'layout'}
                isMobile={this.props.isMobile}
              />
              <p>{t('layoutMobileText')}</p>
            </div>
            <div className="transformation-box carousel-location">
              <h3>{t('Carousel location')}</h3>
              <Select
                onChange={sel => this.setValue('carouselLocation', sel.value)}
                options={carouselLocationOpts}
                value={carouselLocationOpts.find(o => o.value == this.props.config.carouselLocation)}
                isDisabled={displayProps.columns > 1}
                className="select"
                classNamePrefix
              />
            </div>
            <div className="transformation-box button-shape">
              <h3>{t('shape')}</h3>
              <RadioButtons
                items={shapeOptions}
                value={navigationButtonProps.shape}
                setValue={this.setValue}
                propertyName={'shape'}
                disabled={displayProps.mode !== 'classic'}
              />
            </div>
            <div className="transformation-box color-pickers">
              <h3>{t('color')}</h3>
              <ColorPicker
                name={t('background')}
                color={navigationButtonProps.color}
                setColor={this.setValue}
                propertyName={'color'}
                disabled={displayProps.mode !== 'classic'}
              />
              <ColorPicker
                name={t('iconColorLabel')}
                color={navigationButtonProps.iconColor}
                setColor={this.setValue}
                propertyName={'iconColor'}
                disabled={displayProps.mode !== 'classic'}
              />
            </div>
            <EditorLink onClick={() => {
              this.props.setFormConfig(this.props.config);
            }}/>
          </div>
        </div>
      </div>
    );
  }
}

export default EasyCustomization;
